import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { exchangeSessionFromAuth0 } from "../lib/api/client";
import { isApiConfigured } from "../lib/api/config";

const MAX_EXCHANGE_RETRIES = 20;

/**
 * Exchanges Auth0 access tokens for an API httpOnly session cookie.
 * Render only under `Auth0Provider`.
 */
export function SessionBridge() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, getIdTokenClaims, user } = useAuth0();
  const [retryKey, setRetryKey] = useState(0);
  const [tabBump, setTabBump] = useState(0);
  const lastTabExchangeRef = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setRetryKey(0);
    }
  }, [isAuthenticated, user?.sub]);

  useEffect(() => {
    if (!isAuthenticated || !isApiConfigured()) return;
    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastTabExchangeRef.current < 120_000) return;
      lastTabExchangeRef.current = now;
      setTabBump((n) => n + 1);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isApiConfigured()) return;
    if (isLoading) return;
    if (!isAuthenticated || !user?.sub) return;

    let cancelled = false;

    void (async () => {
      try {
        await exchangeSessionFromAuth0({ getAccessTokenSilently, getIdTokenClaims });
        if (!cancelled) {
          setRetryKey((k) => (k > 0 ? 0 : k));
        }
      } catch (e) {
        console.error("Session exchange failed", e);
        if (!cancelled) {
          window.setTimeout(() => {
            setRetryKey((k) => (k < MAX_EXCHANGE_RETRIES ? k + 1 : k));
          }, 2500);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    isAuthenticated,
    user?.sub,
    getAccessTokenSilently,
    getIdTokenClaims,
    retryKey,
    tabBump,
  ]);

  return null;
}
